import arbreFieldJson from "../../../../lib/inventory/data/fields-tree.json";
import alignementFieldJson from "../../../../lib/inventory/data/alignement-fields.json";
import { Step } from '../../shared/types';

const adaptValues = (values: any[]) => {
  return values.map(value => {
    return {
      ...value,
      hidden: value.hidden || false
    }
  }).filter(value => !value.hidden)
}

/**
 * Get field values from the appropriate JSON file based on inventory type
 */
function getFieldValues(fieldName: string, inventaireType?: string): any[] {
  const isAlignement = inventaireType?.toUpperCase() === 'ALIGNEMENT';
  const fieldJson = isAlignement ? alignementFieldJson : arbreFieldJson;
  const field = (fieldJson as Record<string, any>)[fieldName];
  if (field?.freeforms) {
    const freeforms = field?.freeforms.map((freeform: any) => {
      return {
        ...freeform,
        hidden: freeform.hidden || false
      }
    }).filter((freeform: any) => !freeform.hidden)
    return [...adaptValues(field?.values || []), ...freeforms]
  }
  return adaptValues(field?.values || []);
}

/**
 * Get steps configuration based on inventory type
 * @param inventaireType - Type of inventory ('ARBRE' or 'ALIGNEMENT'). Defaults to 'ARBRE'
 */
export function getDetailsSteps(inventaireType?: string): Step[] {
  const isAlignement = inventaireType?.toUpperCase() === 'ALIGNEMENT';
  const fieldJson = isAlignement ? alignementFieldJson : arbreFieldJson;

  return [
    {
      id: 'identification',
      title: 'Identification',
      description: 'Informations d\'identification de l\'arbre',
      fields: [
        { name: 'hauteur', label: 'Hauteur', type: 'number', required: false, unit: 'm' },
        { name: 'diametre', label: 'Diamètre', type: 'number', required: false, unit: 'cm' },
        { name: 'espece', label: 'Espèce', type: 'text', required: false },
        { name: 'code_site', label: 'Code site', type: 'text', required: false },
        { name: 'num_sujet', label: 'Numéro sujet', type: 'text', required: false },
        { name: 'img1', label: 'Image principale', type: 'image-upload', required: false },
        { name: 'img2', label: 'Image secondaire', type: 'image-upload', required: false },
        { name: 'img3', label: 'Image tertiaire', type: 'image-upload', required: false }
      ],
      category: 0
    },
    {
      id: 'illustrations-essence',
      title: 'Illustrations essence',
      description: 'Illustration de référence de l\'espèce',
      fields: [],
      category: 0
    },
    {
      id: 'caracteristiques',
      title: 'Caractéristiques',
      description: 'Caractéristiques physiques de l\'arbre',
      fields: [
        { name: 'au-pied-de-l-arbre', label: 'Au pied de l\'arbre', type: 'multiselect', required: false, options: getFieldValues('au-pied-de-l-arbre', inventaireType) },
        { name: 'caract_pied_other', label: 'Au pied de l\'arbre - Autre, précisez', type: 'text', required: false },
        { name: 'caracteristiques-du-sujet-tronc', label: 'Type de tronc', type: 'radio', required: false, options: getFieldValues('caracteristiques-du-sujet-tronc', inventaireType) },
        { name: 'caracteristiques-du-sujet-tronc_tronc-multiples', label: 'Nombre de troncs multiples', type: 'number', required: false },
        { name: 'port-de-larbre', label: 'Port de l\'arbre', type: 'radio', required: false, options: getFieldValues('port-de-larbre', inventaireType) },
        { name: 'stade-de-developpement', label: 'Stade de développement', type: 'radio', required: false, options: getFieldValues('stade-de-developpement', inventaireType) },
        { name: 'critere-remarquable', label: 'Critères remarquables', type: 'multiselect', required: false, options: getFieldValues('critere-remarquable', inventaireType) },
        { name: 'critere-remarquable_autre-precisez', label: 'Critères remarquables - Autre, précisez', type: 'text', required: false }
      ],
      category: 0
    },
    {
      id: 'etat-sanitaire',
      title: 'État sanitaire',
      description: 'Evaluation de la santé de l\'arbre',
      fields: [
        { name: 'etat-sanitaire-systme-racinaire-collet', label: 'Système racinaire et collet', type: 'multiselect', required: false, options: getFieldValues('etat-sanitaire-systme-racinaire-collet', inventaireType) },
        { name: 'etat-sanitaire-systme-racinaire-collet_cavite-nombre', label: 'Cavité - Nombre', type: 'text', required: false },
        { name: 'etat-sanitaire-systme-racinaire-collet_champignons-lignivores', label: 'Champignons lignivores collet', type: 'multiselect', required: false, options: [] },
        { name: 'etat-sanitaire-systme-racinaire-collet_champignons-lignivores_autres', label: 'Champignons autres - Collet', type: 'text', required: false },
        { name: 'etat-sanitaire-systme-racinaire-collet_autre-precisez', label: 'Système racinaire et collet - Autre, précisez', type: 'text', required: false },
        { name: 'etat-sanitaire-tronc', label: 'État du tronc', type: 'multiselect', required: false, options: getFieldValues('etat-sanitaire-tronc', inventaireType) },
        { name: 'etat-sanitaire-tronc_cavit-nombre', label: 'Cavité - Nombre tronc', type: 'text', required: false },
        { name: 'etat_san_tronc_corps_etranger', label: 'Corps étranger', type: 'text', required: false },
        { name: 'etat-sanitaire-tronc_champignons-lignivores', label: 'Champignons lignivores tronc', type: 'multiselect', required: false, options: [] },
        { name: 'etat-sanitaire-tronc_champignons-lignivores_autres', label: 'Champignons autres - Tronc', type: 'text', required: false },
        { name: 'etat-sanitaire-tronc_parasite', label: 'Parasites tronc', type: 'multiselect', required: false, options: [] },
        { name: 'etat-sanitaire-tronc_parasite-other', label: 'Autres parasites tronc', type: 'text', required: false },
        { name: 'etat-sanitaire-tronc_other', label: 'État tronc - Autre, précisez', type: 'text', required: false },
        { name: 'etat-sanitaire-houppier', label: 'État du houppier', type: 'multiselect', required: false, options: getFieldValues('etat-sanitaire-houppier', inventaireType) },
        { name: 'etat-sanitaire-houppier_champignons-lignivores', label: 'Champignons lignivores houppier', type: 'multiselect', required: false, options: [] },
        { name: 'etat-sanitaire-houppier_champignons-lignivores_autres', label: 'Champignons autres - Houppier', type: 'text', required: false },
        { name: 'etat-sanitaire-houppier_parasite', label: 'Parasites houppier', type: 'multiselect', required: false, options: [] },
        { name: 'etat-sanitaire-houppier_parasite-other', label: 'Autres parasites houppier', type: 'text', required: false },
        { name: 'etat-sanitaire-houppier_other', label: 'État houppier - Autre, précisez', type: 'text', required: false },
        { name: 'etat-sanitaire-general', label: 'État physiologique général', type: 'multiselect', required: false, options: getFieldValues('etat-sanitaire-general', inventaireType) },
        { name: 'etat-sanitaire-general_other', label: 'État général - Commentaire', type: 'text', required: false },
        { name: "risque-de-rupture", label: "Risque de rupture", type: "json", required: false, options: [] },
        { name: "risque-de-rupture-generale", label: "Risque de rupture général", type: "multiselect", required: false, options: getFieldValues('risque-de-rupture-generale', inventaireType) },
        { name: "risque-de-rupture-generale_other", label: "Risque de rupture - Commentaire", type: "text", required: false, options: [] }
      ],
      category: 2
    },
    {
      id: 'environnement',
      title: 'Environnement',
      description: 'Contexte environnemental de l\'arbre',
      fields: [
        { name: 'implantation', label: 'Type d\'implantation', type: 'radio', required: false, options: getFieldValues('implantation', inventaireType) },
        { name: 'domaine', label: 'Domaine', type: 'radio', required: false, options: getFieldValues('domaine', inventaireType) },
        { name: 'nuisances-constatees', label: 'Nuisances constatées', type: 'multiselect', required: false, options: getFieldValues('nuisances-constatees', inventaireType) },
        { name: 'proximite-directe', label: 'Proximité directe', type: 'multiselect', required: false, options: getFieldValues('proximite-directe', inventaireType) },
        { name: 'proximite-directe_other', label: 'Proximité directe - Autre, précisez', type: 'text', required: false },

        { name: "dict", label: "Demande de DICT", type: "multiselect", required: false, options: getFieldValues('proximite-directe', inventaireType) },

        { name: 'taux-de-frquentation', label: 'Taux de fréquentation', type: 'radio', required: false, options: getFieldValues('taux-de-frquentation', inventaireType) },
        { name: 'type-de-passage', label: 'Type de passage', type: 'multiselect', required: false, options: getFieldValues('type-de-passage', inventaireType) },
        { name: 'type-de-passage_other', label: 'Type de passage - Autre, précisez', type: 'text', required: false },
        { name: 'accessibilite', label: 'Accessibilité pour travaux', type: 'radio', required: false, options: getFieldValues('accessibilite', inventaireType) },
        { name: 'accessibilite_other', label: 'Accessibilité - Accès difficile, précisez', type: 'text', required: false }
      ],
      category: 3
    },
    {
      id: 'preconisation-travaux',
      title: 'Travaux',
      description: 'Recommandations d\'intervention et maintenance',
      fields: [
        { name: 'preco-travaux-date-travaux', label: 'Échéance des travaux', type: 'multiselect', required: false, options: getFieldValues('preco-travaux-date-travaux', inventaireType) },
        { name: 'preco-travaux-prochaine-visite', label: 'Prochaine visite', type: 'text', required: false, options: getFieldValues('preco-travaux-prochaine-visite', inventaireType) },
        { name: 'preco-travaux-abattage', label: 'Abattage', type: 'radio', required: false, options: getFieldValues('preco-travaux-abattage', inventaireType) },
        { name: 'preco-travaux-collet', label: 'Travaux collet', type: 'multiselect', required: false, options: getFieldValues('preco-travaux-collet', inventaireType) },
        { name: 'preco-travaux-collet_other', label: 'Travaux collet - Autre, précisez', type: 'text', required: false },
        { name: 'preco-travaux-tronc', label: 'Travaux tronc', type: 'multiselect', required: false, options: getFieldValues('preco-travaux-tronc', inventaireType) },
        { name: 'preco-travaux-tronc_other', label: 'Travaux tronc - Autre, précisez', type: 'text', required: false },
        { name: 'preco-travaux-tronc_protection', label: 'Protection tronc', type: 'text', required: false },
        { name: 'preco-travaux-houppier', label: 'Travaux houppier', type: 'multiselect', required: false, options: getFieldValues('preco-travaux-houppier', inventaireType) },
        { name: 'preco-travaux-houppier_other', label: 'Travaux houppier - Autre, précisez', type: 'text', required: false },
        { name: 'preco-travaux-com', label: 'Commentaires travaux', type: 'textarea', required: false },
        { name: 'travaux_commentaire', label: 'Commentaires généraux', type: 'textarea', required: false }
      ],
      category: 4
    },
    {
      id: 'estimation-valeur',
      title: 'Valeur BEVA',
      description: 'Évaluation de la valeur patrimoniale',
      fields: [
        { name: 'valeur-intrinseque-indice-espece', label: 'Indice variété', type: 'number', required: false },
        { name: 'valeur-intrinseque-indice-sanitaire', label: 'Indice sanitaire', type: 'number', required: false },
        { name: 'valeur-intrinseque-indice-esthetique', label: 'Indice esthétique', type: 'number', required: false },
        { name: 'valeur-intrinseque-indice-situation', label: 'Indice de situation', type: 'number', required: false, options: getFieldValues('valeur-intrinseque-indice-situation', inventaireType) },
        { name: 'beva', label: 'Valeur BEVA calculée', type: 'number', required: false, unit: '€' }
      ],
      category: 0
    },
    {
      id: 'historique',
      title: 'Historique',
      description: 'Historique des modifications et interventions',
      fields: [

      ],
      category: 5
    },
    {
      id: 'illustrations-essence',
      title: 'Illustrations essence',
      description: 'Illustrations photographiques de l\'espèce inventoriée',
      fields: [],
      category: 0
    }
  ];
}

/**
 * Default steps configuration for ARBRE type (for backward compatibility)
 */
export const ARBRE_DETAILS_STEPS: Step[] = getDetailsSteps('ARBRE');
